import type { Concept } from '@rover/rsdk/src/concepts';
import capturePageContext from '@rover/utilities/capturePageContext';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';

export type ConsentPurposeFunctional = 'functional';

export type ConsentPurposePerformance = 'performance';

export type ConsentPurposeMarketing = 'marketing';

export type ConsentPurposeStrictlyNecessary = 'strictly-necessary';

export type ConsentPurpose =
  | ConsentPurposeFunctional
  | ConsentPurposePerformance
  | ConsentPurposeMarketing
  | ConsentPurposeStrictlyNecessary;

export abstract class AnalyticsEvent {
  abstract eventName: string;

  abstract consentPurpose: ConsentPurpose;

  abstract concept: Concept;

  eventProperties = {};

  observabilityTags = {};

  // Page-derived properties (current url, referrer, pageCategory) pinned to the
  // page that fired the event. Populated for cross-page events in
  // fireCrossPageEvent(); left empty for on-page events, which let alooma stamp
  // the live values at track time.
  pageContext: Record<string, unknown> = {};

  constructor(
    eventProperties?: Record<string, unknown>,
    observabilityTags?: Record<string, unknown>
  ) {
    this.eventProperties = eventProperties || {};
    this.observabilityTags = observabilityTags || {};
  }

  /**
   * Use this function call in the case that we are tracking an action that triggers a page load.
   * This will ensure that the event fires off on next page load. `key` is as in
   * `storeCrossPageAnalyticsEvent`.
   */
  fireCrossPageEvent(key?: string): void {
    // Capture the page context here, on the page where the event fired, so it is
    // pinned before the event is stored and replayed on the next page load.
    this.pageContext = capturePageContext();
    if (key === undefined) storeCrossPageAnalyticsEvent(this);
    else storeCrossPageAnalyticsEvent(this, key);
  }
}
